import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, track } from "@/lib/api";
import CTAButtons from "@/components/CTAButtons";
import { Users, Snowflake, Check } from "lucide-react";

export default function VehicleDetail() {
  const { slug } = useParams();
  const { data: v, isLoading } = useQuery({ queryKey: ["v", slug], queryFn: () => api.get(`/vehicles/${slug}`).then((r) => r.data) });
  const { data: cats = [] } = useQuery({ queryKey: ["cats"], queryFn: () => api.get("/vehicle-categories").then((r) => r.data.items) });
  const { data: related = [] } = useQuery({ queryKey: ["v-related"], queryFn: () => api.get("/vehicles", { params: { is_active: true, limit: 4 } }).then((r) => r.data.items) });
  const [lightbox, setLightbox] = useState(null);
  useEffect(() => { if (slug) track("vehicle_view", slug); }, [slug]);

  if (isLoading || !v) return <div className="min-h-[60vh] pt-32 text-center text-slate-500">Loading…</div>;
  const cat = cats.find((c) => c.id === v.category_id);

  return (
    <div className="pt-28 pb-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8">
          <div className="rounded-2xl overflow-hidden aspect-[16/10]"><img src={v.image_url} alt={v.name} className="w-full h-full object-cover" /></div>
          {v.gallery?.length > 1 && (
            <div className="mt-3 grid grid-cols-4 gap-3">
              {v.gallery.slice(0, 4).map((src, i) => (
                <button key={i} onClick={() => setLightbox(src)} className="rounded-lg overflow-hidden aspect-[4/3]" data-testid={`v-gallery-${i}`}><img src={src} alt="" className="w-full h-full object-cover" /></button>
              ))}
            </div>
          )}
          <div className="mt-10">
            <div className="text-xs uppercase tracking-[0.24em] text-amber-600">{cat?.name}</div>
            <h1 className="mt-2 font-display text-5xl text-slate-950">{v.name}</h1>
            <div className="mt-4 flex items-center gap-6 text-slate-600">
              <span className="inline-flex items-center gap-2"><Users className="w-4 h-4" /> {v.seats} Seats</span>
              {v.ac && <span className="inline-flex items-center gap-2"><Snowflake className="w-4 h-4" /> AC</span>}
              <span>{v.rental_type}</span>
            </div>
            <p className="mt-6 text-slate-700 leading-relaxed">{v.description}</p>
            {v.features?.length > 0 && (
              <div className="mt-8">
                <h3 className="font-display text-2xl text-slate-950 mb-4">Features</h3>
                <div className="grid sm:grid-cols-2 gap-2">
                  {v.features.map((f, i) => <div key={i} className="flex items-start gap-2 text-slate-700"><Check className="w-4 h-4 mt-1 text-emerald-600" /> {f}</div>)}
                </div>
              </div>
            )}
          </div>
        </div>

        <aside className="lg:col-span-4">
          <div className="lg:sticky lg:top-28 space-y-6">
            <div className="rounded-2xl border border-slate-200 bg-white p-6">
              <div className="text-xs uppercase tracking-[0.24em] text-amber-600">Reserve this vehicle</div>
              <div className="mt-2 font-display text-2xl text-slate-950">{v.name}</div>
              <div className="text-slate-500 mt-1 text-sm">{v.price_hint}</div>
              <div className="mt-6"><CTAButtons label={v.name} sourceRef={v.slug} testIdPrefix="vehicle" /></div>
            </div>
            {related.length > 0 && (
              <div className="rounded-2xl border border-slate-200 bg-white p-6">
                <div className="text-xs uppercase tracking-[0.24em] text-amber-600 mb-3">Related vehicles</div>
                <ul className="space-y-3">
                  {related.filter((r) => r.slug !== v.slug).slice(0, 4).map((r) => (
                    <li key={r.id}><Link className="text-slate-800 hover:text-amber-700 text-sm font-medium" to={`/transportation/${r.slug}`}>{r.name}</Link></li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </aside>
      </div>

      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/90 grid place-items-center p-6" onClick={() => setLightbox(null)}>
          <img src={lightbox} className="max-h-[90vh] max-w-full rounded-lg" alt="" />
        </div>
      )}
    </div>
  );
}
